import { useEffect, useRef } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"

import { queries } from "@productlane/zero/queries"

import { useBrowserNotifications } from "@/lib/hooks/useBrowserNotifications"
import { useSession } from "@/lib/session"

/**
 * Global component that watches for new live chat messages across all threads
 * and shows browser notifications for incoming customer messages.
 *
 * This component should be mounted inside ZeroProvider to have access to Zero queries.
 */
export function LiveChatNotificationWatcher() {
	const { session } = useSession()
	const navigate = useNavigate()
	const { showNotification, requestPermission, permission } =
		useBrowserNotifications()

	// Track which message IDs we've already notified about
	const notifiedMessageIds = useRef<Set<string>>(new Set())
	// Track the initial load to avoid notifying for existing messages
	const isInitialLoad = useRef(true)
	// Track the timestamp when this component mounted
	const mountTime = useRef(Date.now())

	// Query only active live chat sessions with their messages and thread info
	const [sessions] = useQuery(queries.activeLiveChatSessions())

	// Request permission on mount
	useEffect(() => {
		if (permission === "default") {
			void requestPermission()
		}
	}, [permission, requestPermission])

	// Watch for new live chat messages
	useEffect(() => {
		if (!sessions || sessions.length === 0) return

		// Collect all live chat messages from active sessions
		const allMessages: Array<{
			id: string
			content: string
			email?: string
			name?: string
			createdAt: number
			role: string
			threadId: string
			threadTitle?: string
		}> = []

		for (const session of sessions) {
			for (const message of session.messages ?? []) {
				allMessages.push({
					id: message.id,
					content: message.content,
					email: message.email ?? undefined,
					name: message.name ?? undefined,
					createdAt: message.createdAt,
					role: message.role,
					threadId: session.threadId,
					threadTitle: session.thread?.title ?? undefined,
				})
			}
		}

		// On initial load, just mark all existing messages as "seen"
		if (isInitialLoad.current) {
			for (const msg of allMessages) {
				notifiedMessageIds.current.add(msg.id)
			}
			isInitialLoad.current = false
			return
		}

		// Find new messages we haven't notified about
		const newMessages = allMessages.filter((msg) => {
			// Skip if we've already notified
			if (notifiedMessageIds.current.has(msg.id)) return false

			// Skip if message is from before component mounted (stale data coming in)
			if (msg.createdAt < mountTime.current) return false

			// Skip messages from the current user (agent)
			if (msg.email === session?.email) return false

			// Skip agent and system messages - only notify for USER messages
			if (msg.role !== "USER") return false

			// Only notify when tab is not focused
			if (document.hasFocus()) return false

			return true
		})

		// Show notifications for new messages
		for (const msg of newMessages) {
			// Mark as notified
			notifiedMessageIds.current.add(msg.id)

			// Strip HTML tags for notification body
			const plainTextContent = msg.content.replace(/<[^>]*>/g, "").slice(0, 100)

			showNotification({
				title: msg.name || msg.email || "New live chat message",
				body: plainTextContent,
				threadId: msg.threadId,
				tag: `live-chat-${msg.threadId}`,
				onNavigate: (path) => void navigate({ href: path }),
			})
		}

		// Prune notifiedMessageIds to only keep IDs still in the current data
		const currentMessageIds = new Set(allMessages.map((msg) => msg.id))
		for (const id of notifiedMessageIds.current) {
			if (!currentMessageIds.has(id)) {
				notifiedMessageIds.current.delete(id)
			}
		}
	}, [sessions, session?.email, showNotification, navigate])

	// This component doesn't render anything
	return null
}
